/**
 * First-party UTM + entry-point attribution for marketing forms.
 * Captures query params once per session and attaches them to form posts
 * so the landing API can persist them on Firestore `requestContext` / `source`.
 *
 * Aligns with docs-unified omnichannel UTM matrix:
 * utm_source / utm_medium / utm_campaign / utm_content / utm_term
 *
 * Consent gates:
 * - Click IDs (gclid/fbclid/ttclid/msclkid) → hasMarketingConsent()
 * - GA4 client id → hasAnalyticsConsent()
 */

import {
  hasAnalyticsConsent,
  hasMarketingConsent,
} from "@/lib/cookie-consent";

const STORAGE_KEY = "rr_utm_attribution_v1";
/** First-touch store-link snapshot. Separate from form attribution, and not consent-gated. */
export const STORE_LINK_TOUCH_KEY = "rr_store_link_touch_v1";
const STORE_LINK_TOUCH_EVENT = "rr-store-link-touch";

const STORE_LINK_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
] as const;

export type StoreLinkTouch = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
};

const CLICK_ID_KEYS = ["gclid", "fbclid", "ttclid", "msclkid"] as const;

export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
};

export type ClickIdParams = {
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
};

/** Frozen snapshot of UTM + landing + referrer at a touch point. */
export type TouchSnapshot = UtmParams & {
  landingPath?: string;
  landingUrl?: string;
  referrer?: string;
  capturedAt?: string;
};

export type LandingVariant = "waitlist" | "beta" | "launch";

export type AttributionContext = UtmParams &
  ClickIdParams & {
    entryPoint?: string;
    landingPath?: string;
    landingUrl?: string;
    referrer?: string;
    capturedAt?: string;
    gaClientId?: string;
    firstTouch?: TouchSnapshot;
    lastTouch?: TouchSnapshot;
    landingVariant?: LandingVariant;
    timeToSubmitMs?: number;
    storePlatform?: "ios" | "android";
    country?: string;
  };

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

function canUseDom() {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined";
}

function sanitize(value: string | null | undefined, max = 120): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim().slice(0, max);
  return trimmed || undefined;
}

function readStored(): AttributionContext | null {
  if (!canUseDom()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AttributionContext;
  } catch {
    return null;
  }
}

function writeStored(value: AttributionContext) {
  if (!canUseDom()) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore quota / private mode failures.
  }
}

function buildTouchSnapshot(ctx: AttributionContext, at?: string): TouchSnapshot {
  const snap: TouchSnapshot = {};
  for (const key of UTM_KEYS) {
    if (ctx[key]) snap[key] = ctx[key];
  }
  if (ctx.landingPath) snap.landingPath = ctx.landingPath;
  if (ctx.landingUrl) snap.landingUrl = ctx.landingUrl;
  if (ctx.referrer) snap.referrer = ctx.referrer;
  snap.capturedAt = at ?? new Date().toISOString();
  return snap;
}

/** Read GA4 client id from `_ga` cookie (GA1.x.YYYY.ZZZZ → YYYY.ZZZZ). */
export function readGaClientId(): string | undefined {
  if (!canUseDom() || typeof document === "undefined") return undefined;
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match?.[1]) return undefined;
    const raw = decodeURIComponent(match[1]).trim();
    const parts = raw.split(".");
    if (parts.length >= 4) {
      return sanitize(`${parts[parts.length - 2]}.${parts[parts.length - 1]}`, 80);
    }
    return sanitize(raw, 80);
  } catch {
    return undefined;
  }
}

function stripClickIds(ctx: AttributionContext): AttributionContext {
  const next = { ...ctx };
  for (const key of CLICK_ID_KEYS) {
    delete next[key];
  }
  return next;
}

function computeTimeToSubmitMs(capturedAt: string | undefined): number | undefined {
  if (!capturedAt) return undefined;
  const start = Date.parse(capturedAt);
  if (Number.isNaN(start)) return undefined;
  const ms = Date.now() - start;
  if (ms < 0 || ms > 1000 * 60 * 60 * 24 * 7) return undefined;
  return ms;
}

/** Capture UTM params + landing path from the current URL (idempotent merge). */
export function captureUtmFromLocation(
  search?: string,
  pathname?: string,
): AttributionContext {
  if (!canUseDom()) return {};

  const params = new URLSearchParams(search ?? window.location.search);
  const existing = readStored() ?? {};
  const next: AttributionContext = { ...existing };
  const now = new Date().toISOString();

  for (const key of UTM_KEYS) {
    const value = sanitize(params.get(key));
    if (value) next[key] = value;
  }

  if (hasMarketingConsent()) {
    for (const key of CLICK_ID_KEYS) {
      const value = sanitize(params.get(key), 200);
      if (value) next[key] = value;
    }
  } else {
    for (const key of CLICK_ID_KEYS) {
      delete next[key];
    }
  }

  const entryFromQuery = sanitize(params.get("entryPoint") ?? params.get("ep"), 40);
  if (entryFromQuery) next.entryPoint = entryFromQuery;

  if (!next.landingPath) {
    next.landingPath = sanitize(pathname ?? window.location.pathname, 200);
  }
  if (!next.landingUrl) {
    next.landingUrl = sanitize(window.location.href, 500);
  }
  if (!next.referrer && document.referrer) {
    next.referrer = sanitize(document.referrer, 500);
  }
  if (!next.capturedAt) {
    next.capturedAt = now;
  }

  const touch = buildTouchSnapshot(next, now);
  if (!next.firstTouch) {
    next.firstTouch = touch;
  }
  next.lastTouch = touch;

  writeStored(next);
  return next;
}

export function rememberEntryPoint(entryPoint: string | undefined) {
  if (!canUseDom() || !entryPoint) return;
  const sanitized = sanitize(
    entryPoint
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "_"),
    40,
  );
  if (!sanitized) return;
  const existing = readStored() ?? {};
  writeStored({ ...existing, entryPoint: sanitized });
}

/**
 * Attribution ready for form POST: consent-gated click IDs + GA client id,
 * first/last touch, timeToSubmitMs.
 */
export function getAttribution(): AttributionContext {
  if (!canUseDom()) return {};
  let stored = readStored();
  if (!stored) {
    stored = captureUtmFromLocation();
  }

  let next: AttributionContext = { ...stored };

  if (!hasMarketingConsent()) {
    next = stripClickIds(next);
  }

  if (hasAnalyticsConsent()) {
    const ga = readGaClientId();
    if (ga) next.gaClientId = ga;
  } else {
    delete next.gaClientId;
  }

  const tts = computeTimeToSubmitMs(next.capturedAt);
  if (tts !== undefined) next.timeToSubmitMs = tts;

  // Refresh lastTouch at submit/read time.
  next.lastTouch = buildTouchSnapshot(next);

  return next;
}

/** Compact source string for waitlist / lead `source` field (max ~80). */
export function formatAttributionSource(base?: string): string | undefined {
  const attr = getAttribution();
  const baseTrimmed = base?.trim();
  const parts: string[] = [];

  if (baseTrimmed) parts.push(baseTrimmed);

  if (
    attr.entryPoint &&
    (!baseTrimmed || !baseTrimmed.split(":").includes(attr.entryPoint))
  ) {
    parts.push(attr.entryPoint);
  }

  if (attr.utm_source) {
    parts.push(`utm:${attr.utm_source}`);
  }

  if (parts.length === 0) return undefined;
  return parts.join(":").slice(0, 80);
}

/** Payload fragment merged into every form POST body. */
export function attributionPayload(extraEntryPoint?: string): {
  attribution: AttributionContext;
  entryPoint?: string;
  source?: string;
} {
  if (extraEntryPoint) rememberEntryPoint(extraEntryPoint);
  const attribution = getAttribution();
  return {
    attribution,
    entryPoint: attribution.entryPoint,
  };
}

export function withAttribution<T extends Record<string, unknown>>(
  body: T,
  options?: {
    sourceBase?: string;
    entryPoint?: string;
    landingVariant?: LandingVariant;
    waitlistPlatform?: "ios" | "android";
    waitlistCountry?: string;
  },
): T & {
  attribution: AttributionContext;
  entryPoint?: string;
  source?: string;
} {
  const fragment = attributionPayload(options?.entryPoint);
  const attribution: AttributionContext = {
    ...fragment.attribution,
    ...(options?.landingVariant ? { landingVariant: options.landingVariant } : {}),
    ...(options?.waitlistPlatform ? { storePlatform: options.waitlistPlatform } : {}),
    ...(options?.waitlistCountry ? { country: options.waitlistCountry } : {}),
  };

  const source =
    options?.sourceBase !== undefined ||
    attribution.entryPoint ||
    attribution.utm_source
      ? formatAttributionSource(options?.sourceBase)
      : undefined;

  return {
    ...body,
    ...fragment,
    attribution,
    ...(source ? { source } : {}),
  };
}

export function storeLinkTouchFromSearch(
  search: string | URLSearchParams,
): StoreLinkTouch {
  const params =
    typeof search === "string"
      ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
      : search;
  const touch: StoreLinkTouch = {};
  for (const key of STORE_LINK_FIELDS) {
    const value = sanitize(params.get(key), key === "gclid" ? 200 : 120);
    if (value) touch[key] = value;
  }
  return touch;
}

function touchHasValue(touch: StoreLinkTouch | null | undefined): touch is StoreLinkTouch {
  if (!touch) return false;
  return STORE_LINK_FIELDS.some((key) => Boolean(touch[key]));
}

/** Read the first-touch store snapshot. Empty when this session has not captured one. */
export function readStoreLinkTouch(): StoreLinkTouch | null {
  if (!canUseDom()) return null;
  try {
    const raw = sessionStorage.getItem(STORE_LINK_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoreLinkTouch;
    return touchHasValue(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Write utm_* and gclid once, on the first page that has any of them.
 * Later pages, including /app with an empty query, keep this snapshot.
 * Does not consult marketing consent. Form posts still use getAttribution().
 */
export function captureStoreLinkTouch(search?: string): StoreLinkTouch | null {
  if (!canUseDom()) return null;
  const existing = readStoreLinkTouch();
  if (existing) return existing;
  const touch = storeLinkTouchFromSearch(search ?? window.location.search);
  if (!touchHasValue(touch)) return null;
  try {
    sessionStorage.setItem(STORE_LINK_TOUCH_KEY, JSON.stringify(touch));
  } catch {
    // Ignore quota / private mode failures.
    return touch;
  }
  if (typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(STORE_LINK_TOUCH_EVENT));
  }
  return touch;
}

/** Notify store-link hrefs when the first-touch snapshot is written. */
export function subscribeStoreLinkTouch(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(STORE_LINK_TOUCH_EVENT, onChange);
  return () => window.removeEventListener(STORE_LINK_TOUCH_EVENT, onChange);
}

/** Test helper: clear session attribution (no-op without DOM). */
export function clearAttributionStorage() {
  if (!canUseDom()) return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORE_LINK_TOUCH_KEY);
  } catch {
    // ignore
  }
}
